import { useEffect, useRef, useState } from "react";
import {
  LayoutView,
  loadPageLayout,
  renderSurface,
} from "@norkron/web-page-layout";
import { articleApi } from "../../api/articleUnits";
import {
  COVER_WORDMARK_SRC,
  type CoverContent,
  DEFAULT_SUBTITLE,
  drawCover,
  drawRunningChrome,
  encodeBrandedBodyInput,
  HEADER_MARK_SRC,
  loadImage,
  pointContextFor,
  preparePageCanvas,
} from "../../lib/brandedPreview";

/**
 * `<BrandedCanvasPreview>` — the in-page BRANDED document preview that renders on
 * a `<canvas>` and therefore works in EVERY browser regardless of its PDF-viewer
 * setting (it replaces the old `<iframe src=pdf>` preview + its "your browser
 * downloads PDFs" fallback). RULE ZERO: the BODY is laid out by the SAME Zig→WASM
 * engine that emits the branded PDF (`@norkron/web-page-layout` over
 * `pdfz-layout.wasm`); the Cerebr8 cover + running header/footer are reproduced
 * from the plate spec (`src/lib/brandedPreview`). See that module for the
 * architecture + fidelity notes.
 *
 * Data: the SERVER's render-ready branded body block-DTO (`GET
 * /api/documents/:id/branded-docz`, via the mock-aware
 * `articleApi.getBrandedBodyDto`) — the EXACT body IR the branded PDF lays out
 * (raw_html exploded to typed tables/lists, Cerebr8 styles applied server-side),
 * so tables render as real tables not `<table>` source. Fonts: the self-hosted
 * Alexandria `@font-face` the app
 * already loads (glyph shapes match the PDF); we repaint once `document.fonts`
 * settle so page 1 never rasterizes a fallback face.
 */

export interface BrandedCanvasPreviewProps {
  readonly docId: string;
  /** The document title — the cover title + the running-header title. */
  readonly title?: string;
}

type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly message: string }
  | { readonly status: "ready"; readonly pageCount: number };

const ZOOM_STEPS = [0.6, 0.75, 1, 1.25, 1.5] as const;
const DEFAULT_ZOOM_INDEX = 2; // 100%

export function BrandedCanvasPreview({ docId, title }: BrandedCanvasPreviewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const coverRef = useRef<HTMLCanvasElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [zoomIndex, setZoomIndex] = useState<number>(DEFAULT_ZOOM_INDEX);
  const zoom = ZOOM_STEPS[zoomIndex] as number;

  // The decoded body geometry + loaded brand images, held across zoom changes so
  // a zoom repaint never re-fetches or re-runs the wasm layout.
  const viewRef = useRef<LayoutView | null>(null);
  const coverImgRef = useRef<HTMLImageElement | null>(null);
  const markImgRef = useRef<HTMLImageElement | null>(null);

  const coverTitle = title?.trim() ? title.trim() : "Untitled document";

  // Repaint the cover + all body pages at the current zoom from the cached view.
  const paint = (view: LayoutView, currentZoom: number): void => {
    const cover = coverRef.current;
    const body = bodyRef.current;
    if (!cover || !body) return;

    const coverContent: CoverContent = {
      title: coverTitle,
      subtitle: DEFAULT_SUBTITLE,
      wordmark: coverImgRef.current,
    };
    drawCover(preparePageCanvas(cover, currentZoom), coverContent);

    // Body pages via the shipped engine, then the running header/footer over each.
    const canvases = renderSurface(body, view, { zoom: currentZoom });
    canvases.forEach((canvas, i) => {
      canvas.classList.add("wb-pdfpreview__page");
      const ctx = pointContextFor(canvas, currentZoom);
      if (!ctx) return;
      drawRunningChrome(ctx, {
        pageNumber: i + 2, // cover is page 1
        title: coverTitle,
        mark: markImgRef.current,
      });
    });
  };

  // Load-once effect: fetch docz, run the wasm body layout, load brand images.
  useEffect(() => {
    let disposed = false;
    setLoad({ status: "loading" });
    void (async () => {
      try {
        const [branded, module, wordmark, mark] = await Promise.all([
          articleApi.getBrandedBodyDto(docId),
          loadPageLayout(),
          loadImage(COVER_WORDMARK_SRC),
          loadImage(HEADER_MARK_SRC),
        ]);
        if (disposed) return;
        const soa = module.layout(encodeBrandedBodyInput(branded.bodyDto));
        const view = new LayoutView(soa);
        viewRef.current = view;
        coverImgRef.current = wordmark;
        markImgRef.current = mark;
        paint(view, zoom);
        setLoad({ status: "ready", pageCount: view.pageCount });
        // Repaint once web fonts settle so glyphs paint in Alexandria (the family
        // pdfz shaped against) rather than a transient fallback raster.
        const fonts = (globalThis as { document?: { fonts?: FontFaceSet } }).document
          ?.fonts;
        if (fonts?.ready)
          void fonts.ready.then(() => {
            if (!disposed && viewRef.current) paint(viewRef.current, zoom);
          });
      } catch (err) {
        if (disposed) return;
        setLoad({
          status: "error",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      disposed = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  // Repaint on zoom change from the cached view (no re-fetch / re-layout).
  useEffect(() => {
    if (load.status === "ready" && viewRef.current) paint(viewRef.current, zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomIndex, load.status]);

  const canZoomOut = zoomIndex > 0;
  const canZoomIn = zoomIndex < ZOOM_STEPS.length - 1;

  return (
    <div className="wb-pdfpreview__canvaswrap" data-testid="branded-canvas-preview">
      <div className="wb-pdfpreview__zoombar" data-testid="branded-canvas-zoombar">
        <button
          type="button"
          className="wb-icon-btn"
          data-testid="branded-canvas-zoom-out"
          aria-label="Zoom out"
          disabled={!canZoomOut}
          onClick={() => canZoomOut && setZoomIndex((i) => i - 1)}
        >
          −
        </button>
        <span className="wb-pdfpreview__zoomval" data-testid="branded-canvas-zoom-value">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          className="wb-icon-btn"
          data-testid="branded-canvas-zoom-in"
          aria-label="Zoom in"
          disabled={!canZoomIn}
          onClick={() => canZoomIn && setZoomIndex((i) => i + 1)}
        >
          +
        </button>
        {load.status === "ready" ? (
          <span className="wb-pdfpreview__pagecount" data-testid="branded-canvas-pagecount">
            {load.pageCount + 1} pages
          </span>
        ) : null}
      </div>

      <div className="wb-pdfpreview__scroll" data-testid="branded-canvas-scroll" ref={scrollRef}>
        {load.status === "error" ? (
          <div
            className="wb-pdfpreview__error"
            role="alert"
            data-testid="branded-canvas-error"
          >
            Couldn't render the branded preview: {load.message}
          </div>
        ) : null}
        {load.status === "loading" ? (
          <div className="wb-pdfpreview__loading" data-testid="branded-canvas-loading">
            Rendering branded preview…
          </div>
        ) : null}
        {/* The cover canvas + the body pages container. Hidden until ready so the
            empty canvas doesn't flash before the first paint. */}
        <div
          className="wb-pdfpreview__pages"
          data-hidden={load.status !== "ready" ? "true" : undefined}
        >
          <canvas
            ref={coverRef}
            className="wb-pdfpreview__page wb-pdfpreview__page--cover"
            data-testid="branded-canvas-cover"
          />
          <div
            ref={bodyRef}
            className="wb-pdfpreview__body-pages"
            data-testid="branded-canvas-body"
          />
        </div>
      </div>
    </div>
  );
}
